import React from 'react';
import LinkButton from './components/LinkButton';
import { formatLine } from './formatLine';
import {
  isLinkOnlyLine,
  isListItem,
  isNumberedStep,
  isRelatedSectionHeader,
  isSourceLine,
  listItemText,
  numberedStepParts,
  parseRelatedLine,
  sourceLineText
} from './lineParsers';
import { extractUrlFromText } from './urlUtils';

function createListFlush(listBuffer, formatLineFn) {
  return (keyPrefix, elements) => {
    if (listBuffer.length === 0) return;
    elements.push(
      <ul key={`${keyPrefix}-list`} className="message-list">
        {listBuffer.map((item, i) => (
          <li key={`${keyPrefix}-li-${i}`}>{formatLineFn(item, i)}</li>
        ))}
      </ul>
    );
    listBuffer.length = 0;
  };
}

function createRelatedFlush(relatedBufferRef) {
  return (elements) => {
    const relatedBuffer = relatedBufferRef.current;
    if (!relatedBuffer || relatedBuffer.items.length === 0) {
      relatedBufferRef.current = null;
      return;
    }
    elements.push(
      <div key={`related-${relatedBuffer.key}`} className="message-related">
        <p className="message-section-title">Shih edhe</p>
        <div className="message-related-links">
          {relatedBuffer.items.map((item, i) => (
            <LinkButton key={`rel-${i}`} url={item.url} label={item.label} />
          ))}
        </div>
      </div>
    );
    relatedBufferRef.current = null;
  };
}

/**
 * Turn plain-text bot reply into structured React nodes.
 */
export function formatMessageText(text) {
  const lines = String(text || '').split('\n');
  const elements = [];
  const listBuffer = [];
  const relatedBufferRef = { current: null };

  const flushList = createListFlush(listBuffer, formatLine);
  const flushRelated = createRelatedFlush(relatedBufferRef);

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    const relatedBuffer = relatedBufferRef.current;

    if (relatedBuffer) {
      const parsed = parseRelatedLine(trimmed, relatedBuffer);
      if (parsed.type === 'title') {
        relatedBuffer.pendingTitle = parsed.title;
        return;
      }
      if (parsed.type === 'url' && relatedBuffer.pendingTitle) {
        relatedBuffer.items.push({
          label: relatedBuffer.pendingTitle,
          url: parsed.url
        });
        relatedBuffer.pendingTitle = null;
        return;
      }
      if (parsed.type === 'blank') return;
      if (parsed.type === 'end') flushRelated(elements);
    }

    if (isListItem(trimmed)) {
      flushList(index, elements);
      listBuffer.push(listItemText(trimmed));
      return;
    }

    flushList(index, elements);

    if (isRelatedSectionHeader(trimmed)) {
      relatedBufferRef.current = { key: index, items: [], pendingTitle: null };
      return;
    }

    if (isSourceLine(trimmed)) {
      elements.push(
        <div key={index} className="message-source">
          {formatLine(sourceLineText(trimmed), index)}
        </div>
      );
      return;
    }

    if (isLinkOnlyLine(trimmed)) {
      elements.push(
        <div key={index} className="message-link-row">
          <LinkButton url={extractUrlFromText(trimmed)} />
        </div>
      );
      return;
    }

    if (isNumberedStep(trimmed)) {
      const { num, body } = numberedStepParts(trimmed);
      elements.push(
        <div key={index} className="message-step">
          <span className="message-step-num">{num}</span>
          <div className="message-step-body">{formatLine(body, index)}</div>
        </div>
      );
      return;
    }

    if (trimmed === '') {
      elements.push(<div key={index} className="message-spacer" aria-hidden="true" />);
      return;
    }

    elements.push(
      <p key={index} className="message-paragraph">
        {formatLine(line, index)}
      </p>
    );
  });

  flushList('final', elements);
  flushRelated(elements);
  return elements;
}
